import { BAKERY_ID, SERVICES_ENABLED, SITE_NAME, SITE_URL } from "./constants";

const provider = { "@id": BAKERY_ID };

const areaServed = [
  "Mountain View",
  "Silicon Valley",
  "South Bay",
  "Peninsula",
  "San Francisco Bay Area",
];

export function getClassesSchema() {
  return {
    "@type": "Service",
    "@id": `${SITE_URL}/classes#service`,
    name: "Decorating classes",
    url: `${SITE_URL}/classes`,
    serviceType: "Cake decorating workshop",
    description:
      "Cake, cupcake, cookie, and fondant decorating classes in Mountain View. All classes are 2 hours long and taught by a certified Wilton instructor. Cake4Kids volunteer bakers are welcome as well! Sessions are held in a private home in Mountain View, with the option to host at your venue.",
    provider,
    areaServed: {
      "@type": "City",
      name: "Mountain View",
      containedInPlace: {
        "@type": "AdministrativeArea",
        name: "San Francisco Bay Area",
      },
    },
    audience: [
      {
        "@type": "Audience",
        audienceType: "All ages and skill levels",
      },
      {
        "@type": "Audience",
        audienceType: "Cake4Kids volunteer bakers",
      },
    ],
    offers: [
      {
        "@type": "Offer",
        name: "Group session",
        description: "$95 per person, about 4-6 students",
        price: "95",
        priceCurrency: "USD",
        url: `${SITE_URL}/classes`,
      },
      {
        "@type": "Offer",
        name: "Private session (1 student)",
        price: "250",
        priceCurrency: "USD",
        url: `${SITE_URL}/classes`,
      },
      {
        "@type": "Offer",
        name: "Private session (2 students)",
        price: "350",
        priceCurrency: "USD",
        url: `${SITE_URL}/classes`,
      },
      {
        "@type": "Offer",
        name: "Private session (3 students)",
        price: "380",
        priceCurrency: "USD",
        url: `${SITE_URL}/classes`,
      },
    ],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Available decorating class modules",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "Cake decorating",
            description:
              "Learn essential skills to decorate round cakes with smooth, sharp edges. Take home a beautifully customized cake to share with family and friends.",
            provider: {
              "@type": "Organization",
              name: SITE_NAME,
              url: SITE_URL,
            },
            timeRequired: "PT2H",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "Cupcake decorating",
            description:
              "From basic piping techniques to advanced embellishments, you will learn to transform simple cupcakes into edible works of art.",
            provider: {
              "@type": "Organization",
              name: SITE_NAME,
              url: SITE_URL,
            },
            timeRequired: "PT2H",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "Cookie decorating",
            description:
              "This module will guide you through harnessing the power of royal icing to create intricate detailing and achieve that smooth and glossy finish on cookies.",
            provider: {
              "@type": "Organization",
              name: SITE_NAME,
              url: SITE_URL,
            },
            timeRequired: "PT2H",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Course",
            name: "Fondant",
            description:
              "Dive into the world of fondant in this comprehensive module. You will master smooth fondant application to cakes and use various tools to sculpt designs.",
            provider: {
              "@type": "Organization",
              name: SITE_NAME,
              url: SITE_URL,
            },
            timeRequired: "PT2H",
          },
        },
      ],
    },
  };
}

export function getWeddingsSchema() {
  return {
    "@type": "Service",
    "@id": `${SITE_URL}/services/weddings#service`,
    name: "Wedding cakes and dessert tables",
    url: `${SITE_URL}/services/weddings`,
    serviceType: "Wedding bakery",
    description:
      "Custom wedding cakes and dessert collections designed around your wedding style, venue, and color story. Offerings include tiered wedding cakes, dessert table support, and venue-ready delivery and setup.",
    provider,
    areaServed,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Wedding offerings",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Tiered wedding cakes",
            description:
              "Custom flavors and designs for intimate and large receptions, with finishing details selected to match your decor.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Dessert table support",
            description:
              "Pair your cake with cupcakes, cookies, and mini desserts for a complete presentation.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Venue-ready delivery",
            description:
              "Delivery and setup options are coordinated to align with your venue's timing and access requirements.",
          },
        },
      ],
    },
  };
}

export function getCorporateEventsSchema() {
  return {
    "@type": "Service",
    "@id": `${SITE_URL}/services/corporate-events#service`,
    name: "Corporate event desserts",
    url: `${SITE_URL}/services/corporate-events`,
    serviceType: "Corporate dessert catering",
    description:
      "Custom desserts for team gatherings, product launches, and client appreciation events, including branded cookies, tiered cakes, and full dessert spreads.",
    provider,
    areaServed,
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Corporate service options",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Branded desserts",
            description:
              "Add logos, brand colors, and campaign themes to cookies, cupcakes, cakes, and favor-box assortments.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Scalable ordering",
            description:
              "From small team celebrations to high-volume events, orders are planned around headcount, service style, and timeline.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Professional coordination",
            description:
              "Clear communication, reliable scheduling, and delivery options help ensure your event runs smoothly.",
          },
        },
      ],
    },
  };
}

export function getExtraPageSchema(pathname: string) {
  switch (pathname) {
    case "/classes":
      return getClassesSchema();
    case "/services/weddings":
      return SERVICES_ENABLED ? getWeddingsSchema() : null;
    case "/services/corporate-events":
      return SERVICES_ENABLED ? getCorporateEventsSchema() : null;
    default:
      return null;
  }
}
